import React, {useCallback, useEffect, useRef, useState} from "react";
import { Title } from "@simplecitizen/gdl-react-ui-components";
import {
    RegulaDocumentUploadValidation,
} from "@simplecitizen/gdl-regula-components"
import {useAppDispatch, useAppSelector} from "src/hooks/redux-hooks";
import {
  addIdentityDoc,
  removeIdentityDoc,
  setDocCaptureData
} from "src/store/slices/identityDocCapture";
import { removeDoc } from "src/store/slices/uploadedNonIdDocsSlice";
import {useController, UseFormTrigger} from "react-hook-form";
import RegulaUploadCard from "./RegulaUploadCard";
import NonRegulaUploadCard from "./NonRegulaUploadCard";
import {DocumentPreview} from "@simplecitizen/gdl-regula-components";
import axios from "axios";
import {compareGivenNames, compareLastNames} from "../../../utils/utils.ts";
import {useParams} from "react-router-dom";
import {setDocumentValidationError} from "../../../store/slices/documentValidationSlice.ts";


interface RequiredDocumentsProps {
   control: any ;
    verificationData?: any;
   trigger: UseFormTrigger<any>;
  selectedStatusType: StatusType | null;
  documentCollectors: DocumentCollector[] | null;
  firstNameRef: React.RefObject<HTMLDivElement | null>
  setDocumentCollectors: React.Dispatch<
    React.SetStateAction<DocumentCollector[] | null>
  >;
  isVevoRequired: boolean;
    selectedWorkflow: string | null
}

const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "https://localhost:5003/api/workright";
const NAME_MISMATCH_MESSAGE = `The name(s) on your passport do not match the Biographical Information in your WorkRight profile.

Please update your first, middle, and/or last name (as applicable) in the Biographical Information section to match your full name as it appears on your passport.`;

const RequiredDocuments = ({
  trigger,
  firstNameRef, verificationData,
  selectedStatusType,
  documentCollectors,
  setDocumentCollectors,
  control, isVevoRequired, selectedWorkflow
}: RequiredDocumentsProps) => {
  const dispatch = useAppDispatch();
  dispatch(
    setDocCaptureData({
      getDocumentCollector: selectedStatusType?.documentCollectors,
    })
  );

  const [warningMessage, setWarningMessage] = useState<string>();
  const [isVerifying, setIsVerifying] = useState(true);
  const [disableStoreButton, setDisableStoreButton] = useState(false);
  const [documentFields, setDocumentFields] = useState<any>(null);
  const [documentErrors, setDocumentErrors] = useState<any>(null);
  const [misMatchFields, setMisMatchFields] = useState<string[]>();
  const [docKey, setDocKey] = useState<any>(null);
  const [error, setIsError] = useState<string>();
  const [forceSubmit, setForceSubmit] = useState(false);
  const [disableForceSubmitButton, setDisableForceSubmitButton] =
      useState(false);


  const client = useAppSelector(
      (state) => state.questionnaire_data.questionnaire?.client
  );

  const selectedIDDocument = useAppSelector(
      (state: any) => state?.identity_doc_capture?.selectedIDDocument
  );

    const questionnaire_data = useAppSelector(
        (state: any) => state?.questionnaire_data?.questionnaire
    );

  const { field: firstName } = useController({
    name: "firstName",
    control,
  });
  const { field: lastName } = useController({
    name: "lastName",
    control,
  });
  const { field: middleName } = useController({
    name: "middleName",
    control,
  });

  const [documentRemoved, setDocumentRemoved] = useState(false);

  const [revalidate, setRevalidate] = useState(false);

    const [hasValidationError, setHasValidationError] = useState(false);

    const { checkId } = useParams<{ checkId: string }>();

    const verifyDocument = useCallback(
      async ({ docFrontSideImage, selfieImage, givenNames, surname, docBackSideImage }: any) => {
        setWarningMessage(undefined);
          setMisMatchFields(undefined);
        setDisableStoreButton(false);
        setIsError(undefined);
        setDocumentErrors(null);
        setIsVerifying(true);
        setDocKey(null);
        setDisableForceSubmitButton(false);
          setDocumentErrors(null);

        const newDocument = docBackSideImage
            ? {
              images: [
                { format: "base64", key: "front", source: docFrontSideImage },
                { format: "base64", key: "back", source: docBackSideImage },
                { format: "base64", key: "selfie", source: selfieImage },
              ],
            }
            : {
              images: [
                { format: "base64", key: "front", source: docFrontSideImage },
                { format: "base64", key: "selfie", source: selfieImage },
              ],
            };

        try {
          const response = await axios.post(
              `${API_BASE_URL}/v1/checks/${checkId}/document-collectors/${documentCollectors?.[0]?._id}/Validate`,
              newDocument
          );

          setIsVerifying(false);
          setDocumentFields(response?.data?.data);
          setDocKey(response?.data?._id);
          setForceSubmit(false);

          dispatch(
              setDocCaptureData({
                identityDocument: response.data,
                submittedIDs: { [selectedIDDocument._id]: response.data?._id || "" },
              })
          );

          if(!givenNames && !surname){
                return
            }

          // handle name mismatch logic...
            let fieldsToUpdate: string[] = [];
            const isGivenNamesMatch = compareGivenNames({
                profileFirstName: firstName?.value ?? "",
                profileMiddleName: middleName?.value ?? "",
                regulaGivenNames: givenNames ?? "",
            });
            const isLastNameMatch = compareLastNames({
                profileLastName: lastName?.value ?? "",
                regulaLastName: surname ?? "",
            });

            if (!isGivenNamesMatch) {
                fieldsToUpdate.push("given-names");
                setWarningMessage(NAME_MISMATCH_MESSAGE);
            }
            if (!isLastNameMatch) {
                fieldsToUpdate.push("surname");
                setWarningMessage(NAME_MISMATCH_MESSAGE);
            }

            if (isLastNameMatch && isGivenNamesMatch) {
                setWarningMessage(undefined);
                fieldsToUpdate = [];
            }
            setMisMatchFields(fieldsToUpdate);

        } catch (error: any) {


            setIsVerifying(false);
            setDisableStoreButton(true);

            if (error.response) {
                setHasValidationError(true);

                // setRegulaDocumentErrorCode(error?.response?.status.toString())
                const returnedDocument = error?.response?.data?.document;
                if(returnedDocument){
                    if(!documentCollectors?.[0]?.config?.preventForcedSubmission){
                        setForceSubmit(true);
                    }

                }

                setDocumentFields(returnedDocument?.data);
                setHasValidationError(true);
                if (!returnedDocument) {
                    setIsError(
                        "Something went wrong while uploading your document. Please try again. If the issue persists, you can retry once more or contact us at workright@fragomen.com."
                    );
                    setDisableForceSubmitButton(true);
                }

                if (error?.response?.data?.errors) {
                    setDocumentErrors(error?.response?.data?.errors);
                }

                if(!givenNames && !surname){
                    return
                }
                // name comparison validation
                let fieldsToUpdate: string[] = [];
                const isGivenNamesMatch = compareGivenNames({
                    profileFirstName: firstName?.value ?? "",
                    profileMiddleName: middleName?.value ?? "",
                    regulaGivenNames: givenNames ?? "",
                });
                const isLastNameMatch = compareLastNames({
                    profileLastName: lastName?.value ?? "",
                    regulaLastName: surname ?? "",
                });

                if (!isGivenNamesMatch) {
                    fieldsToUpdate.push("given-names");
                    setWarningMessage(NAME_MISMATCH_MESSAGE);
                }
                if (!isLastNameMatch) {
                    fieldsToUpdate.push("surname");
                    setWarningMessage(NAME_MISMATCH_MESSAGE);
                }

                if (isLastNameMatch && isGivenNamesMatch) {
                    setWarningMessage(undefined);
                    fieldsToUpdate = [];
                }
                setMisMatchFields(fieldsToUpdate);
                return;
            }
            setIsError(
                "Something went wrong while uploading your document. Please try again. If the issue persists, you can retry once more or contact us at workright@fragomen.com."
            );
            setDisableForceSubmitButton(true);
        }
      },
      [checkId, dispatch, firstName?.value, lastName?.value, middleName?.value, documentCollectors, selectedIDDocument?._id]
  );

    const hasRun = useRef(false);

    useEffect(() => {
        if (
            !hasRun.current &&
            documentCollectors?.[0] &&
            questionnaire_data?.jurisdiction?.name === "AUSTRALIA" &&
            isVevoRequired && selectedWorkflow === "passport"
        ) {
            hasRun.current = true;

            const runValidation = async () => {
                try {
                    await verifyDocument({
                        docFrontSideImage: verificationData?.docFrontSideRawBase64,
                        givenNames: verificationData?.givenNames?.value,
                        surname: verificationData?.surname?.value,
                    });

                    dispatch(addIdentityDoc({ ...documentCollectors?.[0], doc_key: docKey }));
                } catch (err) {
                    console.error(err);
                }
            };

            runValidation();
        }
    }, [
        documentCollectors,
        questionnaire_data?.jurisdiction?.name,
        isVevoRequired,
        verifyDocument,
        verificationData,
        dispatch,
        docKey,
        selectedWorkflow
    ]);

    const updateNamesFromProfile = useCallback(
        (profileMisMatchFields: any[]) => {
            const givenNamesField = profileMisMatchFields?.find(
                (item: any) => item.slug === "given-names"
            );
            const surnameField = profileMisMatchFields?.find(
                (item: any) => item.slug === "surname"
            );
            if (profileMisMatchFields) {
                if (givenNamesField) {
                    const givenNames = givenNamesField?.value?.trim().split(/\s+/);
                    if (givenNames.length === 1) {
                        firstName.onChange(givenNames[0]);
                        middleName.onChange("");
                    } else {
                        const last = givenNames[givenNames?.length - 1];
                        const first = givenNames?.slice(0, -1).join(" ");
                        firstName.onChange(first);
                        middleName.onChange(last);
                    }
                }

                if (surnameField) {
                    lastName.onChange(surnameField?.value);
                }
                setMisMatchFields(undefined);
            }
        },
        [firstName, middleName, lastName]
    );

    const removeDocument = (
    document: DocumentCollector,
    docToRemove: "nonIdentityDocuments" | "identityDocuments"
  ) => {
    setDocumentCollectors((prev) => {
      if (prev) {
        return prev.map((doc) => {
          if (doc._id === document?._id) {
            return {
              ...doc,
              [docToRemove]: undefined,
            };
          }
          return doc;
        });
      }
      return null;
    });
    dispatch(removeDoc(document));
    dispatch(removeIdentityDoc(document));
    dispatch(
        setDocCaptureData({
         identityDocumentForced: {}
        })
    );
  };

    return (
    <div>
      <Title className="mt-3 mb-3">Required Documents</Title>
      <div className="flex flex-col gap-y-6">
        {selectedStatusType &&
          documentCollectors &&
          documentCollectors?.map((document) => (
            <div key={document._id} >
              {
                document._subtype === "identity" && verificationData === null && <RegulaUploadCard removeDocument={removeDocument} document={document}
                control={control} />
              }
              {
                  document._subtype === "identity" && verificationData && !documentRemoved && !revalidate &&
                  <DocumentPreview
                      image={verificationData?.docFrontSideImage}
                      documentNumber={verificationData?.documentNumber?.value}
                      dateOfExpiry={verificationData?.dateOfExpiry?.value}
                      issuingStateName={verificationData?.issuingStateName?.value}
                      onRemove={() => setDocumentRemoved(true)}
                      vevoCheck={ verificationData ? true : false }
                      hasValidationError={hasValidationError}
                      requiresValidation={false}
                      onRevalidate={() =>
                          setRevalidate(true)
                  }
                      documentValidated={
                          (verificationData?.documentNumber?.value ||
                          verificationData?.dateOfExpiry?.value ||
                          verificationData?.issuingStateName?.value) ? true : false
                  }
                  />
                }
                {
                    revalidate && document._subtype === "identity" &&
                    <RegulaDocumentUploadValidation

                        regulaLicense={process.env.REACT_APP_REGULA_LICENSE_KEY}
                        onReplace={()=>{
                        }}
                        disableForceSubmitButton={disableForceSubmitButton}
                        error={error}
                        profileMisMatchFields={misMatchFields}
                        warning={warningMessage}
                        onOpenUploader={() => {

                        }}
                        onCloseUploader={() => {
                            setDocumentFields(undefined);
                        }}
                        onForceDocument={async (fields: any, profileMisMatchFields: any) => {
                            // await forceDocument({
                            //     docFrontSideImage:  fields?.docFrontSideImage?.split(",")[1],
                            //     selfieImage: fields?.selfieImage,
                            //     profileMisMatchFields,
                            // });
                            if(!document?.config?.preventForcedSubmission){
                                setForceSubmit(false);
                            }
                            if(fields){
                                dispatch(
                                    setDocumentValidationError({
                                        firstNameInDocument: fields?.givenNames?.value,
                                        lastNameInDocument: fields?.surname?.value,
                                    })
                                );
                            }
                        }}
                        onStoreDocument={(fields: any, profileMisMatchFields: any) => {
                            console.log("submit on store")
                            if (docKey) {
                                dispatch(addIdentityDoc({ ...document, doc_key: docKey }));
                            }
                            updateNamesFromProfile(profileMisMatchFields);
                            setMisMatchFields(undefined);
                            if(fields){
                                dispatch(
                                    setDocumentValidationError({
                                        firstNameInDocument: fields?.givenNames?.value,
                                        lastNameInDocument: fields?.surname?.value,
                                    })
                                );
                            }
                        }}
                        validate={revalidate}
                        validationErrors={false}
                        disableSaveButton={disableStoreButton}
                        //images={images}
                        uploadAttempts={1}
                        title={document.name.en || ""}
                        facialComparison={document?.config?.facialComparison}
                        color={
                            client && client?.branding?.primaryColor
                                ? client?.branding?.primaryColor
                                : "#14BDF3"
                        }
                        documentFields={documentFields}
                        documentFieldsErrors={documentErrors}
                        isVerifying={isVerifying}
                        settings={{
                            startScreen: !document?.config?.newDocumentRequired,
                        }}
                        onRemove={() => {
                            dispatch(
                                setDocumentValidationError({
                                    disableFirstName: false,
                                    disableLastName: false,
                                })
                            );
                            setDocumentFields(null);
                            if (removeDocument) removeDocument(document, "identityDocuments");
                        }}
                        forceSubmit={forceSubmit}
                        onProcessedFinished={async (fields: any) => {
                            await verifyDocument({
                                docFrontSideImage:  fields?.docFrontSideImage?.split(",")[1],
                                selfieImage: fields?.selfieImage,
                                givenNames: fields?.givenNames?.value,
                                surname: fields?.surname?.value,
                                middleName: fields?.middleName?.value,
                            });
                        }}
                    />
                }

              {
                document._subtype === "identity" && verificationData && documentRemoved && <RegulaUploadCard removeDocument={removeDocument} document={document} control={control} />
              }
              {
                document._subtype === "default" &&  <NonRegulaUploadCard document={document} control={control} removeDocument={removeDocument} />
              }

            </div>
          ))}
      </div>
    </div>
  );
};

export default RequiredDocuments;
